/**
 * ============================================================
 * src/constants/jobs.js  –  Job Board Shared Constants
 * ------------------------------------------------------------
 * Single source of truth for every data structure that is
 * shared across the Job Board feature:
 *
 *   JobBoard.vue        – board columns, export colours/labels,
 *                         party names
 *   CreateJobModal.vue  – stage list, create-stage subset,
 *                         party names, checklist presets,
 *                         stage due-date presets
 *   JobDetailModal.vue  – stage list, snake→title stage map
 *   JobCalendar.vue     – calendar chip limit
 *
 * To change a stage name, add a party, or update a checklist
 * preset you only need to edit this one file.
 *
 * NOTE: Priority constants (JOB_PRIORITY_LEVELS, JOB_PRIORITY_OPTIONS)
 * live in constants/constants.js because they are shared with
 * non-Jobs features.  Import them from there as usual.
 * ============================================================
 */

/* ── Stage and type string enums ─────────────────────────────── */

/**
 * JOB_STAGE_KEYS
 * --------------
 * Flat ordered array of every valid stage key string.
 * Used directly as the `enum` array in API response schemas
 * (Fastify/JSON Schema) so the validation stays in sync with
 * the constants defined in JOB_STAGES below.
 *
 * Rules:
 *   • The order mirrors the left-to-right Kanban column order.
 *   • If a stage is added or renamed, update this array AND the
 *     JOB_STAGES array below so both stay in sync.
 */
export const JOB_STAGE_KEYS = [
  'Created',
  'In Preparation',
  'Ready for Installation',
  'On Schedule',
  'In Progress',
  'Completed'
];

/**
 * JOB_TYPES
 * ---------
 * Flat array of every valid job-type string.
 * Used as the `enum` array in API response schemas so only
 * recognised types are accepted/returned.
 *
 *   'New Install' – brand-new hardware installation at a site
 *   'Maintenance' – repair, upgrade or check of existing equipment
 *
 * Add new types here and the schema enum will update automatically.
 */
export const JOB_TYPES = [
  'New Install',
  'Maintenance'
];

/* ── Stage definitions ─────────────────────────────────────── */

/**
 * JOB_STAGES
 * ----------
 * The six lifecycle stages a job passes through on the board.
 * The order here controls the left-to-right column order on the
 * Kanban board and the top-to-bottom option order in all stage
 * dropdowns.
 *
 *   key   – internal identifier stored on each job record and
 *            used as the value in all <select> elements.
 *   label – human-readable string shown in column headers,
 *            dropdowns and the Excel export Status column.
 *
 * Rename a stage by changing its label here; every component
 * and export that reads this array will update automatically.
 */
export const JOB_STAGES = [
  { key: 'Created',                label: 'Created' },
  { key: 'In Preparation',         label: 'In Preparation' },
  { key: 'Ready for Installation', label: 'Ready for Installation' },
  { key: 'On Schedule',            label: 'On Schedule' },
  { key: 'In Progress',            label: 'In Progress' },
  { key: 'Completed',              label: 'Completed' }
];

/**
 * JOB_CREATE_STAGES
 * -----------------
 * The subset of JOB_STAGES that are valid when first creating a
 * job.  'On Schedule', 'In Progress' and 'Completed' are excluded
 * because they can only be reached once contracted teams have
 * taken over the job – they should not be set at the point of
 * creation.
 *
 * Derived automatically from JOB_STAGES so it never goes out of
 * sync if a stage is added or renamed above.
 */
export const JOB_CREATE_STAGES = JOB_STAGES.filter(s =>
  ['Created', 'In Preparation', 'Ready for Installation'].includes(s.key)
);

/**
 * JOB_STAGE_SNAKE_TO_TITLE
 * ------------------------
 * Maps the snake_case stage keys used in checklist item records
 * (e.g. the 'stage' column returned by the API for checklist rows)
 * to the Title-Case keys used in job.stage_due_dates and JOB_STAGES.
 *
 * Used by JobDetailModal to convert a checklist item's stage string
 * into the key needed to look up stage_due_dates.
 *
 * If a stage is renamed in JOB_STAGES above, update the
 * corresponding value here too.
 */
export const JOB_STAGE_SNAKE_TO_TITLE = {
  created:                'Created',
  in_preparation:         'In Preparation',
  ready_for_installation: 'Ready for Installation',
  on_schedule:            'On Schedule',
  in_progress:            'In Progress',
  completed:              'Completed'
};

/* ── Party / organisation names ─────────────────────────────── */

/**
 * JOB_PARTY_NAMES
 * ---------------
 * Ordered list of partner organisations shown in the "Party Name"
 * selector when adding a JR ID entry to a job (CreateJobModal)
 * and in the JR-partner filter dropdown in the bulk export modal
 * (JobBoard).
 *
 * The first entry ('AD Systems') is the default that is
 * pre-selected whenever the modal opens or resets.
 *
 * This list can be replaced with a dynamic API call to
 * GET /api/parties if party names need to be managed at runtime
 * without a code deployment.
 */
export const JOB_PARTY_NAMES = [
  'AD Systems'
];

/* ── Stage due-date preset schedules ─────────────────────────── */

/**
 * JOB_STAGE_DUE_DATE_PRESETS
 * --------------------------
 * Pre-configured sets of per-stage target days (measured from the
 * job creation date) that let teams quickly assign a full schedule
 * without entering every stage individually.
 *
 *   fast     – tight turnaround; suitable for urgent jobs
 *   standard – normal scheduled workload
 *   extended – complex installs or maintenance requiring extra time
 *
 * Each entry shape:
 *   label {string}  – human-readable name shown in the preset dropdown
 *   days  {Object}  – maps each stage key to a number of days after
 *                     job creation by which that stage should be
 *                     completed.  Stages omitted from a preset are
 *                     left with no target so only tracked stages
 *                     appear in red/green on the form.
 *
 * To add a new preset, append an entry here; CreateJobModal will
 * pick it up automatically from stageDueDatePresetOptions.
 */
export const JOB_STAGE_DUE_DATE_PRESETS = {
  fast: {
    label: 'Fast (1 - 5 days)',
    days: {
      'Created':                1,
      'In Preparation':         2,
      'Ready for Installation': 3,
      'On Schedule':            4,
      'In Progress':            5
    }
  },
  standard: {
    label: 'Standard (2 - 14 days)',
    days: {
      'Created':                2,
      'In Preparation':         5,
      'Ready for Installation': 7,
      'On Schedule':            10,
      'In Progress':            14
    }
  },
  extended: {
    label: 'Extended (5 - 28 days)',
    days: {
      'Created':                5,
      'In Preparation':         10,
      'Ready for Installation': 14,
      'On Schedule':            21,
      'In Progress':            28
    }
  }
};

/* ── Excel export helpers ────────────────────────────────────── */

/**
 * JOB_EXPORT_PRIORITY_COLOURS
 * ---------------------------
 * Maps numeric priority values (1–5) to ARGB fill colours used
 * when generating the bulk Excel export from JobBoard.
 *
 * Format: 'AARRGGBB' (full opacity = 'FF' prefix).
 *
 * Colours are intentionally muted/pastel so the rows are clearly
 * distinguishable without being visually harsh in print or on screen:
 *
 *   5 (Highest) → soft rose/salmon
 *   4 (High)    → light peach/apricot
 *   3 (Medium)  → pale straw/cream-yellow
 *   2 (Low)     → light powder-blue
 *   1 (Lowest)  → off-white / very light grey
 */
export const JOB_EXPORT_PRIORITY_COLOURS = {
  5: 'FFFFC7C7',   /* soft rose    */
  4: 'FFFFDDB0',   /* light peach  */
  3: 'FFFFF5B0',   /* pale straw   */
  2: 'FFB8DCF0',   /* powder blue  */
  1: 'FFF5F5F5'    /* off-white    */
};

/**
 * JOB_EXPORT_PRIORITY_LABELS
 * --------------------------
 * Maps numeric priority values (1–5) to the text label written
 * into the Priority column of the Excel export.
 *
 * Kept separate from JOB_PRIORITY_LEVELS so the export format can
 * diverge from the UI labels without affecting the board UI.
 */
export const JOB_EXPORT_PRIORITY_LABELS = {
  5: 'Highest',
  4: 'High',
  3: 'Medium',
  2: 'Low',
  1: 'Lowest'
};

/* ── Calendar view ───────────────────────────────────────────── */

/**
 * CALENDAR_MAX_CHIPS_PER_DAY
 * --------------------------
 * Maximum number of job chips rendered directly inside a calendar
 * day cell before an overflow "+N more" badge is shown instead.
 *
 * Increasing this number makes cells taller; decreasing it
 * causes overflow earlier.  3 is the default that keeps the
 * calendar readable at standard viewport widths.
 */
export const CALENDAR_MAX_CHIPS_PER_DAY = 3;
